import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import {
  NotificationVariant,
  useNotifications,
} from "../../components/dashboard/NotificationsProvider";
import PaymentMethodApi from "../../api/subscription/PaymentMethodApi";
import SubscriptionAPI from "../../api/subscription/SubscriptionApi";
import TermsApi from "../../api/subscription/TermsApi";
import { formatPrice } from "../facility/utils/tierCalculations";
import { PaymentMethodResponseDto } from "../../types/payment-method";
import type { CaptureSession } from "../../types/payment-method";
import { TermsVersion } from "../../types/terms";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import { generateHumanDisplayName } from "../../utils/DataTransformationUtils";
import { useSelector } from "../../store";
import PaymentTermsConsent from "./PaymentTermsConsent";
import {
  PAYMENT_TERMS_TYPE_KEY,
  recordPaymentTermsAcceptance,
} from "./paymentTermsAcceptance";
import { useTermsFeatureFlag } from "./hooks/useTermsFeatureFlag";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY!);

export type InitialPeriodImpactPreview = {
  willCharge: boolean;
  amountCents: number;
  currency: string;
  periodStart?: string;
  periodEnd?: string;
  description?: string;
  skipReason?: string;
};

export function InitialPeriodChargeCallout({
  preview,
}: {
  preview: InitialPeriodImpactPreview;
}) {
  if (!preview.willCharge) {
    return null;
  }

  const amountLabel = formatPrice(
    preview.amountCents,
    (preview.currency || "USD").toUpperCase(),
  );
  const range =
    preview.periodStart && preview.periodEnd
      ? `${new Date(preview.periodStart).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          timeZone: "UTC",
        })} – ${new Date(preview.periodEnd).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        })}`
      : null;

  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      data-testid="initial-period-charge-callout"
    >
      <Typography variant="body2" fontWeight={600} gutterBottom>
        Your card will be charged today
      </Typography>
      <Typography variant="body2">
        Amount: {amountLabel}
        {range ? ` for ${range}` : ""}.
      </Typography>
      <Typography variant="body2" sx={{ mt: 1 }}>
        An invoice will be created — you can find it under Subscription →
        Billing History.
      </Typography>
    </Alert>
  );
}

function notificationForCreateResult(
  saved: PaymentMethodResponseDto,
  previewWillCharge: boolean,
): { message: string; variant: keyof typeof NotificationVariant } {
  const ip = saved.initialPeriodBilling;

  // Preview said we would charge, but create response omitted billing outcome.
  if (!ip && previewWillCharge) {
    return {
      message:
        "Payment method saved, but we could not confirm the initial period charge. Check Outstanding Balance on this page or refresh.",
      variant: NotificationVariant.warning,
    };
  }

  if (ip?.charged) {
    return {
      message:
        "Payment method saved and initial period charged. See Billing History for your invoice.",
      variant: NotificationVariant.success,
    };
  }
  if (ip?.skipReason === "error") {
    return {
      message:
        "Payment method saved, but we could not complete the initial period charge. Refresh and check Outstanding Balance or contact support.",
      variant: NotificationVariant.warning,
    };
  }
  const chargeIncomplete =
    ip?.skipReason === "charge_failed" ||
    (!!ip &&
      !ip.unlocked &&
      !ip.charged &&
      (ip.amountCents > 0 || previewWillCharge));
  if (chargeIncomplete) {
    return {
      message:
        "Payment method saved, but the initial period charge did not complete. Pay the open invoice under Outstanding Balance on this page to finish setup.",
      variant: NotificationVariant.warning,
    };
  }
  if (ip && ip.amountCents > 0 && !ip.unlocked) {
    return {
      message:
        "Payment method saved. Check Outstanding Balance or Billing History for your initial period invoice.",
      variant: NotificationVariant.warning,
    };
  }
  return {
    message: "Payment method saved successfully",
    variant: NotificationVariant.success,
  };
}

const StripeCardForm = ({
  clientSecret,
  onClose,
  refresh,
  orgId,
  willCharge,
  saveDisabled,
  onRecordPaymentTerms,
}: {
  clientSecret: string;
  onClose: () => void;
  refresh: () => void;
  orgId: string;
  willCharge: boolean;
  saveDisabled: boolean;
  onRecordPaymentTerms: () => Promise<void>;
}) => {
  const paymentMethodApi = useMemo(() => new PaymentMethodApi(), []);
  const stripe = useStripe();
  const elements = useElements();
  const theme = useTheme();
  const { setNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSave = async () => {
    if (!stripe || !elements || saveDisabled || !cardComplete) return;

    const card = elements.getElement(CardElement);
    if (!card) {
      setNotification({
        message: "Enter a valid card number, expiry date, and CVC.",
        variant: NotificationVariant.error,
      });
      return;
    }

    setLoading(true);

    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card,
        },
      });

      if (result.error) {
        setNotification({
          message: result.error.message,
          variant: NotificationVariant.error,
        });
        setLoading(false);
        return;
      }

      const paymentMethodId = result.setupIntent?.payment_method;
      if (typeof paymentMethodId !== "string" || !paymentMethodId) {
        setNotification({
          message: "Enter a valid card number, expiry date, and CVC.",
          variant: NotificationVariant.error,
        });
        setLoading(false);
        return;
      }

      await onRecordPaymentTerms();
      const saved = await paymentMethodApi.createPaymentMethod({
        orgId,
        processor: "stripe",
        paymentMethodId,
      });

      if (saved) {
        setNotification(notificationForCreateResult(saved, willCharge));
        setLoading(false);
        onClose();
        refresh();
        return;
      }
      setNotification({
        message: "Failed to save payment method",
        variant: NotificationVariant.error,
      });
    } catch (error) {
      setNotification({
        message: getApiErrorMessage(error, "Unable to save payment method."),
        variant: NotificationVariant.error,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardElement
        onChange={(event) => setCardComplete(event.complete)}
        options={{
          style: {
            base: {
              fontSize: "16px",
              color: theme.palette.text.primary,
              "::placeholder": {
                color: theme.palette.text.secondary,
              },
            },
            invalid: {
              color: theme.palette.error.main,
            },
          },
        }}
      />

      <DialogActions
        sx={{
          mt: "auto",
          pt: 2,
          px: 0,
          flexShrink: 0,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || saveDisabled || !cardComplete}
          onClick={handleSave}
          data-testid="save-payment-method"
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {loading ? "Saving..." : "Save Payment Method"}
        </Button>
      </DialogActions>
    </Box>
  );
};

const DimePayCardForm = ({
  session,
  onClose,
  refresh,
  orgId,
  willCharge,
  saveDisabled,
  onRecordPaymentTerms,
}: {
  session: CaptureSession;
  onClose: () => void;
  refresh: () => void;
  orgId: string;
  willCharge: boolean;
  saveDisabled: boolean;
  onRecordPaymentTerms: () => Promise<void>;
}) => {
  const paymentMethodApi = useMemo(() => new PaymentMethodApi(), []);
  const { setNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [vaultedCardToken, setVaultedCardToken] = useState<string | null>(null);
  const paymentStarted = useRef(false);
  const autoSaveStarted = useRef(false);

  const refreshVerification = useCallback(async () => {
    const token = session.cardRequestToken;
    if (!token || verified) return;
    try {
      const status = await paymentMethodApi.getDimepayCardStatus(token);
      if (status.verified && status.token?.startsWith("card_")) {
        setVerified(true);
        setVaultedCardToken(status.token);
      }
    } catch {
      // Stay waiting until DimePay reports SUCCESS / card_…
    }
  }, [paymentMethodApi, session.cardRequestToken, verified]);

  useEffect(() => {
    if (!session.cardRequestToken || verified || saveDisabled) return undefined;
    void refreshVerification();
    const id = window.setInterval(() => {
      void refreshVerification();
    }, 3000);
    return () => window.clearInterval(id);
  }, [refreshVerification, saveDisabled, session.cardRequestToken, verified]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const host = (() => {
        try {
          return new URL(event.origin).hostname;
        } catch {
          return "";
        }
      })();
      if (!host.endsWith("dimepay.app") && !host.endsWith("dimepay.net")) {
        return;
      }
      void refreshVerification();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [refreshVerification]);

  const handleSave = useCallback(async () => {
    if (saveDisabled || !verified || paymentStarted.current) return;
    const token = vaultedCardToken;
    if (!token || !token.startsWith("card_")) {
      setNotification({
        message:
          "Finish Save card in the DimePay form so the card is verified before Medial charges it.",
        variant: NotificationVariant.error,
      });
      return;
    }
    paymentStarted.current = true;
    setSaveFailed(false);
    setLoading(true);
    try {
      await onRecordPaymentTerms();
      const saved = await paymentMethodApi.createPaymentMethod({
        orgId,
        processor: "dimepay",
        dimepayToken: token,
        cardRequestToken: session.cardRequestToken,
      });
      if (saved) {
        setNotification(notificationForCreateResult(saved, willCharge));
        onClose();
        refresh();
        return;
      }
      paymentStarted.current = false;
      autoSaveStarted.current = false;
      setSaveFailed(true);
      setNotification({
        message: "Failed to save payment method",
        variant: NotificationVariant.error,
      });
    } catch (error) {
      paymentStarted.current = false;
      autoSaveStarted.current = false;
      setSaveFailed(true);
      setNotification({
        message: getApiErrorMessage(
          error,
          "The card was verified, but Medial could not complete the sale.",
        ),
        variant: NotificationVariant.error,
      });
    } finally {
      setLoading(false);
    }
  }, [
    onClose,
    onRecordPaymentTerms,
    orgId,
    paymentMethodApi,
    refresh,
    saveDisabled,
    session.cardRequestToken,
    setNotification,
    vaultedCardToken,
    verified,
    willCharge,
  ]);

  useEffect(() => {
    if (
      saveDisabled ||
      !verified ||
      !vaultedCardToken?.startsWith("card_") ||
      autoSaveStarted.current ||
      saveFailed
    ) {
      return;
    }
    autoSaveStarted.current = true;
    void handleSave();
  }, [handleSave, saveDisabled, saveFailed, vaultedCardToken, verified]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {saveDisabled ? (
        <Alert
          severity="info"
          sx={{ mb: 2, flexShrink: 0 }}
          data-testid="dimepay-accept-terms-prompt"
        >
          Accept Payment Terms & Conditions to enter your card.
        </Alert>
      ) : session.cardUrl ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
            Use <strong>Save card</strong> in the DimePay form to verify the
            card. After DimePay verifies it, Medial saves the card and then
            processes the sale. If 3-D Secure fails here,{" "}
            <Box
              component="a"
              href={session.cardUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 600 }}
            >
              open the form in a new tab
            </Box>
            .
          </Alert>
          <Box
            component="iframe"
            title="DimePay card form"
            src={session.cardUrl}
            allow="payment *; publickey-credentials-get *"
            referrerPolicy="strict-origin-when-cross-origin"
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              border: 0,
              borderRadius: 1,
            }}
          />
        </Box>
      ) : (
        <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
          DimePay did not return a card form URL. Restart the subscription
          service after setting keys, then try again.
        </Alert>
      )}
      <DialogActions
        sx={{
          mt: "auto",
          pt: 2,
          px: 0,
          flexShrink: 0,
          flexWrap: "wrap",
          gap: 1,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mr: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
        >
          {loading && <CircularProgress size={16} color="inherit" />}
          <Typography variant="body2" color="text.secondary">
            {loading
              ? willCharge
                ? "Card verified. Processing the sale…"
                : "Card verified. Saving payment method…"
              : verified
                ? saveFailed
                  ? "Card verified. Click Save to try again."
                  : "Card verified. Click Save to finish."
                : saveDisabled
                  ? "Accept payment terms to continue."
                  : "Waiting for DimePay verification…"}
          </Typography>
        </Stack>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            loading ||
            saveDisabled ||
            !verified ||
            !vaultedCardToken?.startsWith("card_")
          }
          onClick={handleSave}
          data-testid="save-payment-method"
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {loading ? "Saving..." : "Save Payment Method"}
        </Button>
      </DialogActions>
    </Box>
  );
};

export function AddPaymentMethodModal({
  open,
  onClose,
  orgId,
  onSaved,
  clientSecret,
  captureSession,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onSaved: () => void;
  clientSecret: string;
  captureSession?: CaptureSession | null;
}) {
  const subscriptionApi = useMemo(() => new SubscriptionAPI(), []);
  const termsApi = useMemo(() => new TermsApi(), []);
  const { featureEnabled, flagReady } = useTermsFeatureFlag();
  const profile = useSelector((state) => state.user.profile);
  const practitionerReference = useSelector(
    (state) => state.user.practitionerReference,
  );
  const signerName =
    generateHumanDisplayName(profile?.name) ||
    practitionerReference?.display ||
    "";
  const [preview, setPreview] = useState<InitialPeriodImpactPreview | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [previewRetryNonce, setPreviewRetryNonce] = useState(0);
  const [paymentTerms, setPaymentTerms] = useState<TermsVersion | null>(null);
  const [paymentTermsLoading, setPaymentTermsLoading] = useState(false);
  const [paymentTermsError, setPaymentTermsError] = useState(false);
  const [paymentTermsRetryNonce, setPaymentTermsRetryNonce] = useState(0);
  const [paymentTermsAlreadyAccepted, setPaymentTermsAlreadyAccepted] =
    useState(false);
  const [agreedToPaymentTerms, setAgreedToPaymentTerms] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!open || !orgId) {
      setPreview(null);
      setPreviewError(false);
    } else {
      setPreviewLoading(true);
      setPreviewError(false);
      subscriptionApi
        .calculateInitialPeriodImpact(orgId)
        .then((data) => {
          if (!cancelled) {
            setPreview(data);
            setPreviewError(false);
          }
        })
        .catch(() => {
          if (!cancelled) {
            // Do not imply willCharge=false — amount is unknown and a charge may still apply.
            setPreview(null);
            setPreviewError(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setPreviewLoading(false);
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [open, orgId, subscriptionApi, previewRetryNonce]);

  useEffect(() => {
    let cancelled = false;
    if (!open) {
      setAgreedToPaymentTerms(false);
      setPaymentTerms(null);
      setPaymentTermsError(false);
      setPaymentTermsAlreadyAccepted(false);
    } else if (!flagReady) {
      setPaymentTerms(null);
      setPaymentTermsLoading(false);
      setPaymentTermsError(false);
    } else if (!featureEnabled || !orgId) {
      setPaymentTerms(null);
      setPaymentTermsLoading(false);
      setPaymentTermsError(false);
    } else {
      setPaymentTermsLoading(true);
      setPaymentTermsError(false);
      termsApi
        .getCurrent(PAYMENT_TERMS_TYPE_KEY)
        .then(async (current) => {
          if (cancelled) {
            return;
          }
          setPaymentTerms(current);
          if (!current || !orgId) {
            setPaymentTermsAlreadyAccepted(false);
            return;
          }
          try {
            const status = await termsApi.getAcceptanceStatus(
              orgId,
              PAYMENT_TERMS_TYPE_KEY,
            );
            if (!cancelled) {
              setPaymentTermsAlreadyAccepted(status?.accepted === true);
            }
          } catch {
            if (!cancelled) {
              setPaymentTermsAlreadyAccepted(false);
            }
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPaymentTerms(null);
            setPaymentTermsError(true);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setPaymentTermsLoading(false);
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [
    open,
    orgId,
    featureEnabled,
    flagReady,
    termsApi,
    paymentTermsRetryNonce,
  ]);

  if (!open) return null;

  const previewReady = !previewLoading && !previewError && preview !== null;
  const paymentTermsReady =
    flagReady &&
    (!featureEnabled || (!paymentTermsLoading && !paymentTermsError));
  const showPaymentTerms = featureEnabled && !!paymentTerms;
  const saveDisabled =
    !previewReady ||
    !paymentTermsReady ||
    (showPaymentTerms && !agreedToPaymentTerms);

  const handleRecordPaymentTerms = async () => {
    if (!paymentTerms?.id) {
      return;
    }
    await recordPaymentTermsAcceptance({
      accept: (versionId, payload) => termsApi.accept(versionId, payload),
      organizationId: orgId,
      versionId: paymentTerms.id,
      alreadyAccepted: paymentTermsAlreadyAccepted,
    });
  };

  const showTermsPane =
    featureEnabled &&
    (paymentTermsLoading || paymentTermsError || !!paymentTerms);

  const paymentForm = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {previewLoading && (
        <CircularProgress
          size={24}
          sx={{ display: "block", my: 2 }}
          data-testid="initial-period-preview-loading"
        />
      )}
      {previewError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="initial-period-preview-error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setPreviewRetryNonce((n) => n + 1)}
            >
              Retry
            </Button>
          }
        >
          We couldn&apos;t load today&apos;s charge amount. Retry before saving
          your card — saving may still charge your initial billing period.
        </Alert>
      )}
      {previewReady && preview && (
        <InitialPeriodChargeCallout preview={preview} />
      )}
      {captureSession?.processor === "dimepay" &&
        previewReady &&
        paymentTermsReady && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <DimePayCardForm
              session={captureSession}
              orgId={orgId}
              onClose={onClose}
              refresh={onSaved}
              willCharge={!!preview?.willCharge}
              saveDisabled={saveDisabled}
              onRecordPaymentTerms={handleRecordPaymentTerms}
            />
          </Box>
        )}
      {captureSession?.processor !== "dimepay" &&
        clientSecret &&
        previewReady &&
        paymentTermsReady && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              pt: 1,
            }}
          >
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripeCardForm
                orgId={orgId}
                clientSecret={clientSecret}
                onClose={onClose}
                refresh={onSaved}
                willCharge={!!preview?.willCharge}
                saveDisabled={saveDisabled}
                onRecordPaymentTerms={handleRecordPaymentTerms}
              />
            </Elements>
          </Box>
        )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      data-testid="add-payment-method-dialog"
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "90%", md: "70%" },
          maxWidth: { xs: "90%", md: 1000 },
          height: { xs: "90%", md: "min(80vh, 720px)" },
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>Add Payment Method</DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            flex: 1,
            minHeight: 0,
            gap: { xs: 2, md: 0 },
          }}
        >
          {showTermsPane && (
            <Box
              data-testid="add-payment-method-terms-pane"
              sx={{
                flex: { xs: "0 1 auto", md: 1 },
                minWidth: 0,
                minHeight: 0,
                maxHeight: { xs: "36%", md: "none" },
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                pr: { md: 3 },
                mr: { md: 3 },
                borderRight: { md: 1 },
                borderColor: { md: "divider" },
              }}
            >
              {paymentTermsError && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                  data-testid="payment-terms-error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => setPaymentTermsRetryNonce((n) => n + 1)}
                    >
                      Retry
                    </Button>
                  }
                >
                  We couldn&apos;t load Payment Terms & Conditions. Retry before
                  saving your card.
                </Alert>
              )}
              <PaymentTermsConsent
                terms={paymentTerms}
                loading={featureEnabled && paymentTermsLoading}
                agreed={agreedToPaymentTerms}
                onAgreedChange={setAgreedToPaymentTerms}
                signerName={signerName}
              />
            </Box>
          )}
          <Box
            data-testid="add-payment-method-form-pane"
            sx={{
              flex: { xs: "1 1 auto", md: 1 },
              minWidth: 0,
              minHeight: { xs: 220, md: 0 },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {paymentForm}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default AddPaymentMethodModal;
